<template>
  <div class="meeting-status-modal-wrap w-full h-full">
    <woot-modal
      :show="show"
      :on-close="onClose"
      content-class="meeting-status-modal"
    >
      <woot-modal-header :header-title="modalTitle" />

      <div class="meeting-status-body">
        <div class="meeting-status-subject">
          {{ meetingSubjectLabel }}
        </div>

        <div class="meeting-status-field">
          <label class="meeting-status-label">
            {{ $t('OVERVIEW_REPORTS.PRIORITY_LEADS.DEMO_COMPLETED_LABEL') }}
            <span class="meeting-status-required">*</span>
          </label>
          <div class="meeting-status-radio-group">
            <label
              class="meeting-status-radio-option"
              :class="{ 'is-selected': demoCompleted === true }"
            >
              <input
                v-model="demoCompleted"
                type="radio"
                class="meeting-status-radio-input"
                :value="true"
              />
              <span>{{
                $t('OVERVIEW_REPORTS.PRIORITY_LEADS.DEMO_COMPLETED_YES')
              }}</span>
            </label>
            <label
              class="meeting-status-radio-option"
              :class="{ 'is-selected': demoCompleted === false }"
            >
              <input
                v-model="demoCompleted"
                type="radio"
                class="meeting-status-radio-input"
                :value="false"
              />
              <span>{{
                $t('OVERVIEW_REPORTS.PRIORITY_LEADS.DEMO_COMPLETED_NO')
              }}</span>
            </label>
          </div>
        </div>

        <div v-if="demoCompleted === true" class="meeting-status-field">
          <label class="meeting-status-label">
            {{ $t('OVERVIEW_REPORTS.PRIORITY_LEADS.VERY_GOOD_DEMO_LABEL') }}
            <span class="meeting-status-required">*</span>
          </label>
          <div class="meeting-status-radio-group">
            <label
              class="meeting-status-radio-option"
              :class="{ 'is-selected': veryGoodDemo === true }"
            >
              <input
                v-model="veryGoodDemo"
                type="radio"
                class="meeting-status-radio-input"
                :value="true"
              />
              <span>{{
                $t('OVERVIEW_REPORTS.PRIORITY_LEADS.DEMO_COMPLETED_YES')
              }}</span>
            </label>
            <label
              class="meeting-status-radio-option"
              :class="{ 'is-selected': veryGoodDemo === false }"
            >
              <input
                v-model="veryGoodDemo"
                type="radio"
                class="meeting-status-radio-input"
                :value="false"
              />
              <span>{{
                $t('OVERVIEW_REPORTS.PRIORITY_LEADS.DEMO_COMPLETED_NO')
              }}</span>
            </label>
          </div>
        </div>

        <div class="meeting-status-field">
          <label class="meeting-status-label">
            {{ demoDescriptionFieldLabel }}
            <span class="meeting-status-required">*</span>
          </label>
          <textarea
            v-model="demoDescription"
            class="meeting-status-textarea"
            rows="4"
            :placeholder="demoDescriptionFieldPlaceholder"
          />
        </div>
      </div>

      <div class="meeting-status-footer">
        <woot-button
          :is-disabled="isSubmitDisabled"
          :is-loading="isSubmitting"
          @click.prevent="submit"
        >
          {{ saveButtonLabel }}
        </woot-button>
      </div>
    </woot-modal>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import ContactMeetingsAPI from 'dashboard/api/contactMeetings';

export default {
  mixins: [alertMixin],
  props: {
    contact: {
      type: Object,
      required: true,
    },
    meetingId: {
      type: [Number, String],
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      demoCompleted: null,
      veryGoodDemo: null,
      demoDescription: '',
      isSubmitting: false,
      loadRequestId: 0,
      demoStatusRecorded: false,
    };
  },
  computed: {
    modalTitle() {
      return this.demoStatusRecorded
        ? this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.MEETING_STATUS_EDIT_TITLE')
        : this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.MEETING_STATUS_TITLE');
    },
    saveButtonLabel() {
      return this.demoStatusRecorded
        ? this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.MEETING_STATUS_UPDATE')
        : this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.MEETING_STATUS_SAVE');
    },
    meetingSubjectLabel() {
      const attrs = this.contact?.additional_attributes || {};
      return (
        attrs.demo_subject ||
        attrs.meeting_description ||
        this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.MEETING_STATUS_DEFAULT_SUBJECT')
      );
    },
    isSubmitDisabled() {
      if (this.demoCompleted === null || this.isSubmitting) return true;
      if (this.demoCompleted === true && this.veryGoodDemo === null) return true;
      if (!String(this.demoDescription || '').trim()) return true;
      return false;
    },
    demoDescriptionFieldLabel() {
      if (this.demoCompleted === false) {
        return this.$t(
          'OVERVIEW_REPORTS.PRIORITY_LEADS.DEMO_NOT_COMPLETED_REASON_LABEL'
        );
      }
      return this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.DEMO_DESCRIPTION_LABEL');
    },
    demoDescriptionFieldPlaceholder() {
      if (this.demoCompleted === false) {
        return this.$t(
          'OVERVIEW_REPORTS.PRIORITY_LEADS.DEMO_NOT_COMPLETED_REASON_PLACEHOLDER'
        );
      }
      return this.$t(
        'OVERVIEW_REPORTS.PRIORITY_LEADS.DEMO_DESCRIPTION_PLACEHOLDER'
      );
    },
  },
  watch: {
    demoCompleted(completed) {
      if (completed !== true) {
        this.veryGoodDemo = false;
      }
    },
    show: {
      immediate: true,
      handler(isOpen) {
        if (!isOpen) return;
        this.applySavedValues(this.contact);
        this.refreshSavedFormState();
      },
    },
    meetingId() {
      if (!this.show) return;
      this.applySavedValues(this.contact);
      this.refreshSavedFormState();
    },
  },
  methods: {
    applySavedValues(source) {
      const recorded = !!source?.demo_status_recorded;
      this.demoStatusRecorded = recorded;
      const completed = source?.demo_completed;
      if (
        recorded &&
        (completed === true || completed === false)
      ) {
        this.demoCompleted = completed;
      } else {
        this.demoCompleted = null;
      }
      this.demoDescription = source?.demo_description || '';
      const veryGood = source?.very_good_demo;
      if (recorded && completed === true && (veryGood === true || veryGood === false)) {
        this.veryGoodDemo = veryGood;
      } else if (completed === true) {
        this.veryGoodDemo = null;
      } else {
        this.veryGoodDemo = false;
      }
    },
    savedValuesFromMeetingMetadata(metadata) {
      if (!metadata || typeof metadata !== 'object') return null;

      const recorded = metadata.demo_status_recorded;
      if (!recorded) return null;

      return {
        demo_status_recorded: true,
        demo_completed: metadata.demo_completed,
        demo_description: metadata.demo_description,
        very_good_demo: metadata.very_good_demo,
      };
    },
    async refreshSavedFormState() {
      if (!this.contact?.id || !this.meetingId) return;

      const requestId = this.loadRequestId + 1;
      this.loadRequestId = requestId;

      try {
        const { data } = await ContactMeetingsAPI.getMeeting(
          this.contact.id,
          this.meetingId
        );
        if (requestId !== this.loadRequestId) return;

        const fromRoot = data?.demo_status_recorded ? data : null;
        const fromMetadata = this.savedValuesFromMeetingMetadata(data?.metadata);
        this.applySavedValues(fromRoot || fromMetadata || this.contact);
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        this.applySavedValues(this.contact);
      }
    },
    onClose() {
      this.$emit('close');
    },
    async submit() {
      if (this.isSubmitDisabled || !this.contact?.id || !this.meetingId) {
        return;
      }

      this.isSubmitting = true;
      try {
        const { data } = await ContactMeetingsAPI.recordDemoStatus(
          this.contact.id,
          this.meetingId,
          {
            demo_completed: this.demoCompleted,
            demo_description: this.demoDescription.trim(),
            very_good_demo: this.demoCompleted === true ? this.veryGoodDemo : false,
          }
        );
        this.showAlert(
          this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.MEETING_STATUS_SUCCESS'),
          { success: true }
        );
        if (typeof bus !== 'undefined' && bus && bus.$emit) {
          bus.$emit('demo-meeting-updated', data);
        }
        this.$emit('success', data);
        this.onClose();
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.MEETING_STATUS_ERROR');
        this.showAlert(message, { success: false });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.meeting-status-body {
  @apply px-8 pb-2;
  min-height: 20rem;
}

.meeting-status-subject {
  @apply mb-5 px-3 py-2.5 text-sm leading-snug text-slate-700 dark:text-slate-200
    bg-slate-50 dark:bg-slate-800/80 border border-slate-100 dark:border-slate-700 rounded-lg;
}

.meeting-status-field + .meeting-status-field {
  @apply mt-5;
}

.meeting-status-label {
  @apply block mb-2 text-sm font-medium text-slate-800 dark:text-slate-100;
}

.meeting-status-required {
  @apply text-red-500;
}

.meeting-status-radio-group {
  @apply flex flex-wrap gap-3;
}

.meeting-status-radio-option {
  @apply inline-flex items-center gap-2 px-4 py-2.5 text-sm cursor-pointer
    border border-slate-200 dark:border-slate-600 rounded-lg
    bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200
    transition-colors duration-150;

  &:hover {
    @apply border-slate-300 dark:border-slate-500;
  }

  &.is-selected {
    @apply border-woot-500 bg-woot-25 dark:bg-woot-900/20 text-woot-800 dark:text-woot-500;
  }
}

.meeting-status-radio-input {
  @apply m-0 cursor-pointer;
}

.meeting-status-textarea {
  @apply w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600
    bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100
    focus:outline-none focus:ring-2 focus:ring-woot-200 dark:focus:ring-woot-800;
  min-height: 108px;
  resize: vertical;
}

.meeting-status-footer {
  @apply flex justify-end gap-2 px-8 py-5 mt-2 border-t border-slate-75 dark:border-slate-700;
}
</style>

<style lang="scss">
.meeting-status-modal-wrap {
  .modal-container.meeting-status-modal {
    @apply rounded-xl overflow-hidden;
    max-width: 32rem;
  }

  .modal-container.meeting-status-modal .modal-header {
    @apply pb-0;
  }
}
</style>
